/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: marker-position <-> value math for the report scales.
// Source: cardiac_report_v0_5_6_dev_2.html lines 3373-3418 (cardiac cycle marker math),
// 3593-3706 (format/observed/marker-to-value), 3992-4119 (calculateMarkerPosition,
// displayMarkerPosition).
// Deviation: currentHeartRateBpm() global -> explicit hrBpm argument.

import { UNIDIRECTIONAL_ZONES, BIDIRECTIONAL_ZONES } from "./constants"
import { parseNormalRangeSpec, parseSdObservedRatio, parseSysDiaDurationObserved } from "./parse"
import { cardiacCycleRatioZonesForHeartRate, expectedSysDiaRangesForHeartRate } from "./zones"
import { isCardiacCycleTimingFeature, isSysDiaDurationFeature, isRhythmRegularityFeature } from "./features"
import {
  isTimingVariationText,
  rhythmRegularityPatternLabel,
  rhythmRegularitySwingLabel,
  rhythmRegularitySwingPct,
  rhythmRegularitySwingText,
  rhythmRegularityThresholds,
} from "./timing-pattern"
import { getColorClass, cardiacCycleTimingColorClass, __setCardiacMarkerStatusHook } from "./status"

export function formatSystoleDiastoleDisplay(sdRatio) {
  if (!Number.isFinite(sdRatio) || sdRatio <= 0) return null
  return `1 : ${(1 / sdRatio).toFixed(1)}`
}

export function cardiacCycleMarkerPositionFromRatio(ratio, hrBpm) {
  if (!Number.isFinite(ratio)) return 50
  const z = cardiacCycleRatioZonesForHeartRate(hrBpm)
  const b = BIDIRECTIONAL_ZONES
  if (ratio < z.redLow) {
    const pct = Math.max(0, ratio) / z.redLow
    return pct * b.redLowEnd
  }
  if (ratio < z.normalLow) {
    const pct = (ratio - z.redLow) / (z.normalLow - z.redLow)
    return b.redLowEnd + pct * (b.amberLowEnd - b.redLowEnd)
  }
  if (ratio <= z.normalHigh) {
    const pct = (ratio - z.normalLow) / (z.normalHigh - z.normalLow)
    return b.amberLowEnd + pct * (b.greenEnd - b.amberLowEnd)
  }
  if (ratio <= z.redHigh) {
    const pct = (ratio - z.normalHigh) / (z.redHigh - z.normalHigh)
    return b.greenEnd + pct * (b.amberHighEnd - b.greenEnd)
  }
  const pct = Math.min(1, (ratio - z.redHigh) / (z.maxHigh - z.redHigh))
  return b.amberHighEnd + pct * (100 - b.amberHighEnd)
}

export function cardiacCycleRatioFromMarkerPosition(markerPos, hrBpm) {
  const pos = Math.min(100, Math.max(0, markerPos))
  const z = cardiacCycleRatioZonesForHeartRate(hrBpm)
  const b = BIDIRECTIONAL_ZONES
  if (pos < b.redLowEnd) {
    return (pos / b.redLowEnd) * z.redLow
  }
  if (pos < b.amberLowEnd) {
    const pct = (pos - b.redLowEnd) / (b.amberLowEnd - b.redLowEnd)
    return z.redLow + pct * (z.normalLow - z.redLow)
  }
  if (pos <= b.greenEnd) {
    const pct = (pos - b.amberLowEnd) / (b.greenEnd - b.amberLowEnd)
    return z.normalLow + pct * (z.normalHigh - z.normalLow)
  }
  if (pos <= b.amberHighEnd) {
    const pct = (pos - b.greenEnd) / (b.amberHighEnd - b.greenEnd)
    return z.normalHigh + pct * (z.redHigh - z.normalHigh)
  }
  const pct = (pos - b.amberHighEnd) / (100 - b.amberHighEnd)
  return z.redHigh + pct * (z.maxHigh - z.redHigh)
}

// Original quirk preserved: marker position -> ratio -> "1 : x.x" display string
// -> re-parsed ratio (with toFixed(1) rounding) -> color class.
__setCardiacMarkerStatusHook(function (markerPos, hrBpm) {
  return cardiacCycleTimingColorClass(
    formatSystoleDiastoleDisplay(cardiacCycleRatioFromMarkerPosition(markerPos, hrBpm)),
    null,
    hrBpm
  )
})

export function observedNumericValue(feature, observed) {
  if (isCardiacCycleTimingFeature(feature)) return parseSdObservedRatio(observed)
  if (isSysDiaDurationFeature(feature)) {
    const parsed = parseSysDiaDurationObserved(observed)
    if (Number.isFinite(parsed.diaMs)) return parsed.diaMs
  }
  const observedCleaned = String(observed)
    .replace(/[%<>≤≥ms:bp]/g, "")
    .trim()
  if (observedCleaned.includes("–")) {
    const observedParts = observedCleaned.split("–").map((p) => parseFloat(p.trim()))
    return (observedParts[0] + observedParts[1]) / 2
  }
  return parseFloat(observedCleaned)
}

export function formatObservedValue(feature, value) {
  if (!Number.isFinite(value)) return null
  if (isCardiacCycleTimingFeature(feature)) return formatSystoleDiastoleDisplay(value)
  if (feature.type === "percentage") return Math.max(0, Math.min(100, Math.round(value))) + "%"
  if (feature.type === "ratio") return value.toFixed(2)
  if (feature.type === "bpm") return `${Math.round(value)} bpm`
  if (feature.type === "duration") return `${Math.round(value)} ms`
  return value.toFixed(1)
}

export function calculateObservedValueFromMarkerPosition(feature, markerPos, hrBpm) {
  if (isCardiacCycleTimingFeature(feature)) {
    return formatSystoleDiastoleDisplay(cardiacCycleRatioFromMarkerPosition(markerPos, hrBpm))
  }
  if (isSysDiaDurationFeature(feature)) {
    // The duration card carries two measurements. A marker move places both
    // at the same relative point of their own heart-rate-adjusted bands, so
    // the pair stays coherent and the card never collapses to one value.
    const range = expectedSysDiaRangesForHeartRate(hrBpm)
    const b = BIDIRECTIONAL_ZONES
    const interpolate = (from, to, fraction) => from + (to - from) * Math.max(0, Math.min(1, fraction))
    const valueForPosition = (min, max) => {
      if (markerPos < b.redLowEnd) return interpolate(min * 0.55, min * 0.8, markerPos / b.redLowEnd)
      if (markerPos < b.amberLowEnd)
        return interpolate(min * 0.8, min, (markerPos - b.redLowEnd) / (b.amberLowEnd - b.redLowEnd))
      if (markerPos <= b.greenEnd)
        return interpolate(min, max, (markerPos - b.amberLowEnd) / (b.greenEnd - b.amberLowEnd))
      if (markerPos <= b.amberHighEnd)
        return interpolate(max, max * 1.2, (markerPos - b.greenEnd) / (b.amberHighEnd - b.greenEnd))
      return interpolate(max * 1.2, max * 1.5, (markerPos - b.amberHighEnd) / (100 - b.amberHighEnd))
    }
    return `SYS ${Math.round(valueForPosition(range.sysMin, range.sysMax))} ms · DIA ${Math.round(
      valueForPosition(range.diaMin, range.diaMax)
    )} ms`
  }
  if (isRhythmRegularityFeature(feature)) {
    // The marker reads as a timing swing on the card's bands, in the card's
    // own wording, so a moved marker and a typed value are the same kind of
    // result
    const thresholds = rhythmRegularityThresholds(feature, hrBpm)
    const swing = rhythmRegularitySwingFromMarkerPosition(markerPos, thresholds)
    return rhythmRegularitySwingText(swing, rhythmRegularitySwingLabel(swing, thresholds))
  }

  // GUARD: If normalRange is empty, return the existing observed value unchanged
  if (!feature.normalRange || String(feature.normalRange).trim() === "") {
    return feature.observed || "—"
  }

  const spec = parseNormalRangeSpec(feature.normalRange, feature.type)
  if (!spec || !Number.isFinite(spec.min) || !Number.isFinite(spec.max)) {
    return feature.observed || "—"
  }
  const normMin = spec.min
  const normMax = spec.max
  const range = normMax - normMin
  if (!Number.isFinite(range) || range <= 0) return feature.observed || "—"
  let observedValue
  const uniGreenEnd = UNIDIRECTIONAL_ZONES.greenEnd
  const uniAmberEnd = UNIDIRECTIONAL_ZONES.amberEnd
  const biRedLowEnd = BIDIRECTIONAL_ZONES.redLowEnd
  const biAmberLowEnd = BIDIRECTIONAL_ZONES.amberLowEnd
  const biGreenEnd = BIDIRECTIONAL_ZONES.greenEnd
  const biAmberHighEnd = BIDIRECTIONAL_ZONES.amberHighEnd

  if (spec.kind === "lower" && feature.scaleType === "unidirectional") {
    if (markerPos <= uniGreenEnd) {
      const pct = markerPos / uniGreenEnd
      observedValue = normMax - (normMax - normMin) * pct
    } else if (markerPos <= uniAmberEnd) {
      const pct = (markerPos - uniGreenEnd) / (uniAmberEnd - uniGreenEnd)
      observedValue = normMin - range * pct * 0.2
    } else {
      const pct = (markerPos - uniAmberEnd) / (100 - uniAmberEnd)
      observedValue = normMin - range * (0.2 + pct * 0.8)
    }
    return formatObservedValue(feature, observedValue)
  }

  if (feature.scaleType === "unidirectional") {
    if (markerPos <= uniGreenEnd) {
      const pct = markerPos / uniGreenEnd
      observedValue = normMin + range * pct
    } else if (markerPos <= uniAmberEnd) {
      const pct = (markerPos - uniGreenEnd) / (uniAmberEnd - uniGreenEnd)
      observedValue = normMax + range * pct * 0.2
    } else {
      const pct = (markerPos - uniAmberEnd) / (100 - uniAmberEnd)
      observedValue = normMax + range * (0.2 + pct * 0.8)
    }
  } else {
    if (markerPos >= biAmberLowEnd && markerPos <= biGreenEnd) {
      const pct = (markerPos - biAmberLowEnd) / (biGreenEnd - biAmberLowEnd)
      observedValue = normMin + range * pct
    } else if (markerPos >= biRedLowEnd && markerPos < biAmberLowEnd) {
      const pct = (markerPos - biRedLowEnd) / (biAmberLowEnd - biRedLowEnd)
      observedValue = normMin - range * 0.2 * (1 - pct)
    } else if (markerPos > biGreenEnd && markerPos <= biAmberHighEnd) {
      const pct = (markerPos - biGreenEnd) / (biAmberHighEnd - biGreenEnd)
      observedValue = normMax + range * 0.2 * pct
    } else if (markerPos < biRedLowEnd) {
      const pct = markerPos / biRedLowEnd
      observedValue = normMin - range * (0.8 - pct * 0.6)
    } else {
      const pct = (markerPos - biAmberHighEnd) / (100 - biAmberHighEnd)
      observedValue = normMax + range * (0.2 + pct * 0.6)
    }
  }

  return formatObservedValue(feature, observedValue) || feature.observed || "—"
}

const finiteNumber = (value) => value !== null && value !== undefined && Number.isFinite(Number(value))

/**
 * The ± timing swing at the right-hand end of a Cycle Regularity scale
 * (Heart Report v0.5.8 at cardiac-panel-v4 git ebcfc04): the scale runs
 * from 0% through the card's Normal and Elevated bands to 25%.
 */
export const RHYTHM_REGULARITY_SCALE_MAX_SWING_PCT = 25

/**
 * The swing a marker position stands for: the inverse of
 * rhythmRegularityMarkerPosition on the same bands — the green zone spans
 * 0 to the Normal band, amber to the Elevated band, red to the scale's 25%
 * end. Rounded to the 0.1% the card shows.
 */
export function rhythmRegularitySwingFromMarkerPosition(markerPos, thresholds) {
  const zones = UNIDIRECTIONAL_ZONES
  const maximum = RHYTHM_REGULARITY_SCALE_MAX_SWING_PCT
  const position = Math.max(0, Math.min(100, Number(markerPos)))
  let swing
  if (position <= zones.greenEnd) {
    swing = (position / zones.greenEnd) * thresholds.normal
  } else if (position <= zones.amberEnd) {
    swing =
      thresholds.normal +
      ((position - zones.greenEnd) / (zones.amberEnd - zones.greenEnd)) * (thresholds.elevated - thresholds.normal)
  } else {
    swing =
      thresholds.elevated + ((position - zones.amberEnd) / (100 - zones.amberEnd)) * (maximum - thresholds.elevated)
  }
  return Math.round(Math.max(0, Math.min(maximum, swing)) * 10) / 10
}

/**
 * Where a Cycle Regularity card's quoted ± timing swing sits on its scale:
 * within the green zone up to the normal band, across the amber zone up to
 * the elevated band, then across the red to the scale's 25% end; null when
 * the card quotes no swing.
 */
export function rhythmRegularityMarkerPosition(feature, hrBpm) {
  const swing = rhythmRegularitySwingPct(feature)
  if (swing === null) return null
  const thresholds = rhythmRegularityThresholds(feature, hrBpm)
  const zones = UNIDIRECTIONAL_ZONES
  if (swing <= thresholds.normal) {
    return Math.max(5, Math.min(zones.greenEnd - 2, (swing / thresholds.normal) * zones.greenEnd))
  }
  if (swing <= thresholds.elevated) {
    return (
      zones.greenEnd +
      ((swing - thresholds.normal) / (thresholds.elevated - thresholds.normal)) * (zones.amberEnd - zones.greenEnd)
    )
  }
  return (
    zones.amberEnd +
    Math.min(
      1,
      (swing - thresholds.elevated) / Math.max(RHYTHM_REGULARITY_SCALE_MAX_SWING_PCT - thresholds.elevated, 1)
    ) *
      (100 - zones.amberEnd)
  )
}

export function calculateMarkerPosition(feature, hrBpm) {
  if (isCardiacCycleTimingFeature(feature)) {
    // A card whose value spans the recordings it includes marks the
    // representative median of those recordings, not the range's midpoint
    const ratio =
      feature.clinicianRatioRange && finiteNumber(feature.representativeSdRatio)
        ? Number(feature.representativeSdRatio)
        : parseSdObservedRatio(feature.observed)
    return cardiacCycleMarkerPositionFromRatio(ratio, hrBpm)
  }
  if (
    isSysDiaDurationFeature(feature) &&
    feature.clinicianSysDiaDurationRange &&
    (finiteNumber(feature.representativeSysMs) || finiteNumber(feature.representativeDiaMs))
  ) {
    const phase = (value) => (finiteNumber(value) ? `${Math.round(Number(value))} ms` : "—")
    return calculateMarkerPosition(
      {
        ...feature,
        observed: `SYS ${phase(feature.representativeSysMs)} · DIA ${phase(feature.representativeDiaMs)}`,
        clinicianSysDiaDurationRange: null,
      },
      hrBpm
    )
  }
  if (isRhythmRegularityFeature(feature)) {
    // The quoted swing places the marker: the imported one until the
    // reviewer acts, then the reviewer's own (a moved marker or a typed
    // value is itself a swing). A reviewer result in the older
    // percent-regular wording keeps the position it was given.
    const swingMarkerPosition =
      !feature.reviewTouched || isTimingVariationText(feature.observed)
        ? rhythmRegularityMarkerPosition(feature, hrBpm)
        : null
    if (swingMarkerPosition !== null) return swingMarkerPosition
    const patternLabel = rhythmRegularityPatternLabel(feature, feature.status)
    const timingVar = Number(feature.timingVariabilityPct)
    const expected = Number(feature.expectedNormalVariabilityPct)
    const irregular = Number(feature.irregularVariabilityPct)
    // Imported metrics place the marker only until the reviewer moves it or
    // types a value; after that the reviewer's result owns the position.
    if (
      !feature.reviewTouched &&
      Number.isFinite(timingVar) &&
      Number.isFinite(expected) &&
      Number.isFinite(irregular) &&
      expected > 0 &&
      irregular > expected
    ) {
      if (timingVar <= expected) {
        return Math.max(
          5,
          Math.min(UNIDIRECTIONAL_ZONES.greenEnd - 2, (timingVar / expected) * UNIDIRECTIONAL_ZONES.greenEnd)
        )
      }
      if (timingVar <= irregular) {
        return (
          UNIDIRECTIONAL_ZONES.greenEnd +
          ((timingVar - expected) / (irregular - expected)) *
            (UNIDIRECTIONAL_ZONES.amberEnd - UNIDIRECTIONAL_ZONES.greenEnd)
        )
      }
      return (
        UNIDIRECTIONAL_ZONES.amberEnd +
        Math.min(1, (timingVar - irregular) / Math.max(irregular, 1)) * (100 - UNIDIRECTIONAL_ZONES.amberEnd)
      )
    }
    if (patternLabel === "Expected PCG timing variability") return UNIDIRECTIONAL_ZONES.greenEnd / 2
    if (patternLabel === "Moderate PCG timing variability" || patternLabel === "Periodic PCG timing pattern")
      return (UNIDIRECTIONAL_ZONES.greenEnd + UNIDIRECTIONAL_ZONES.amberEnd) / 2
    if (patternLabel === "High PCG timing variability") return (UNIDIRECTIONAL_ZONES.amberEnd + 100) / 2
    const observedValue = observedNumericValue(feature, feature.observed)
    if (!Number.isFinite(observedValue)) return 50
    if (observedValue >= 85) {
      return ((100 - Math.min(100, observedValue)) / 15) * 58
    }
    if (observedValue >= 65) {
      return 58 + ((85 - observedValue) / 20) * 22
    }
    return 80 + ((65 - Math.max(0, observedValue)) / 65) * 20
  }

  // Get the color classification first
  const colorClass = getColorClass(
    feature.name,
    feature.observed,
    feature.normalRange,
    feature.status,
    feature.scaleType,
    hrBpm
  )

  const isUnidirectional = feature.scaleType === "unidirectional"

  // GUARD: If normalRange is empty, use status-based positioning directly
  if (!feature.normalRange || String(feature.normalRange).trim() === "") {
    // Place marker in the center of the appropriate zone based on status
    if (isUnidirectional) {
      if (colorClass === "green") return UNIDIRECTIONAL_ZONES.greenEnd / 2
      if (colorClass === "amber") return (UNIDIRECTIONAL_ZONES.greenEnd + UNIDIRECTIONAL_ZONES.amberEnd) / 2
      return (UNIDIRECTIONAL_ZONES.amberEnd + 100) / 2
    } else {
      if (colorClass === "green") return (BIDIRECTIONAL_ZONES.amberLowEnd + BIDIRECTIONAL_ZONES.greenEnd) / 2
      if (colorClass === "amber") return (BIDIRECTIONAL_ZONES.redLowEnd + BIDIRECTIONAL_ZONES.amberLowEnd) / 2
      return BIDIRECTIONAL_ZONES.redLowEnd / 2
    }
  }

  // Parse the observed value to get a relative position within the status zone
  const observedValue = observedNumericValue(feature, feature.observed)

  // Parse normal range
  const rangeSpec = parseNormalRangeSpec(feature.normalRange, feature.type)
  if (!rangeSpec) return 50
  const normMin = rangeSpec.min
  const normMax = rangeSpec.max

  // Safeguard against invalid ranges
  if (isNaN(normMin) || isNaN(normMax) || normMin === Infinity || normMax === Infinity) {
    return 50 // Return center position as fallback
  }

  const range = normMax - normMin
  if (range <= 0 || isNaN(range)) {
    return 50 // Return center position as fallback
  }

  if (rangeSpec.kind === "lower" && isUnidirectional) {
    if (observedValue >= normMin) {
      const posInNormal = (normMax - observedValue) / range
      return Math.min(UNIDIRECTIONAL_ZONES.greenEnd, Math.max(0, posInNormal * UNIDIRECTIONAL_ZONES.greenEnd))
    }
    const deficit = normMin - observedValue
    if (deficit <= range * 0.2) {
      return (
        UNIDIRECTIONAL_ZONES.greenEnd +
        Math.min(
          UNIDIRECTIONAL_ZONES.amberEnd - UNIDIRECTIONAL_ZONES.greenEnd,
          Math.max(0, (deficit / (range * 0.2)) * (UNIDIRECTIONAL_ZONES.amberEnd - UNIDIRECTIONAL_ZONES.greenEnd))
        )
      )
    }
    return (
      UNIDIRECTIONAL_ZONES.amberEnd +
      Math.min(
        100 - UNIDIRECTIONAL_ZONES.amberEnd,
        Math.max(0, ((deficit - range * 0.2) / (range * 0.8)) * (100 - UNIDIRECTIONAL_ZONES.amberEnd))
      )
    )
  }

  if (isUnidirectional) {
    const bufferRange = range * 0.2 // 20% buffer for borderline

    if (colorClass === "green") {
      const posInNormal = (observedValue - normMin) / range // 0 to 1
      return posInNormal * UNIDIRECTIONAL_ZONES.greenEnd
    } else if (colorClass === "amber") {
      const posInBorderline = (observedValue - normMax) / bufferRange
      return (
        UNIDIRECTIONAL_ZONES.greenEnd +
        Math.min(
          UNIDIRECTIONAL_ZONES.amberEnd - UNIDIRECTIONAL_ZONES.greenEnd,
          Math.max(0, posInBorderline * (UNIDIRECTIONAL_ZONES.amberEnd - UNIDIRECTIONAL_ZONES.greenEnd))
        )
      )
    } else {
      const aboveBufferRange = normMax + bufferRange
      const posInAbnormal = Math.min(1, (observedValue - aboveBufferRange) / (range * 0.8))
      return UNIDIRECTIONAL_ZONES.amberEnd + posInAbnormal * (100 - UNIDIRECTIONAL_ZONES.amberEnd)
    }
  } else {
    if (colorClass === "green") {
      const normalRange = normMax - normMin
      const posInRange = (observedValue - normMin) / normalRange
      return (
        BIDIRECTIONAL_ZONES.amberLowEnd + posInRange * (BIDIRECTIONAL_ZONES.greenEnd - BIDIRECTIONAL_ZONES.amberLowEnd)
      )
    } else if (colorClass === "amber") {
      if (observedValue < normMin) {
        const pct = Math.min(1, Math.max(0, (observedValue - (normMin - range * 0.2)) / (range * 0.2)))
        return BIDIRECTIONAL_ZONES.redLowEnd + pct * (BIDIRECTIONAL_ZONES.amberLowEnd - BIDIRECTIONAL_ZONES.redLowEnd)
      } else {
        const pct = Math.min(1, Math.max(0, (observedValue - normMax) / (range * 0.2)))
        return BIDIRECTIONAL_ZONES.greenEnd + pct * (BIDIRECTIONAL_ZONES.amberHighEnd - BIDIRECTIONAL_ZONES.greenEnd)
      }
    } else {
      if (observedValue < normMin) {
        const pct = Math.min(1, Math.max(0, (observedValue - (normMin - range * 0.8)) / (range * 0.6)))
        return pct * BIDIRECTIONAL_ZONES.redLowEnd
      } else {
        const pct = Math.min(1, Math.max(0, (observedValue - (normMax + range * 0.2)) / (range * 0.6)))
        return BIDIRECTIONAL_ZONES.amberHighEnd + pct * (100 - BIDIRECTIONAL_ZONES.amberHighEnd)
      }
    }
  }
}

/** Where one end of a heart-rate range sits on the Heart Rate card's scale. */
export function heartRateMarkerPosition(feature, bpm, hrBpm) {
  return calculateMarkerPosition(
    { ...feature, observed: `${bpm} bpm`, markerPosition: undefined, reviewTouched: false },
    hrBpm
  )
}

/** The marker position of a range card: midway between its two ends. */
export function heartRateRangeMarkerPosition(feature, range, hrBpm) {
  return (heartRateMarkerPosition(feature, range.min, hrBpm) + heartRateMarkerPosition(feature, range.max, hrBpm)) / 2
}

export function displayMarkerPosition(position) {
  const value = Number(position)
  if (!Number.isFinite(value)) return 50
  return Math.max(5, Math.min(95, value))
}

export function cardiacCycleDisplayFromRatioOrObserved(rawRatio, observed) {
  const observedText = String(observed || "").trim()
  // A "1 : x" value, or a "1 : x–y" range, is already in display form
  if (/^1[:/]\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?$/.test(observedText.replace(/\s+/g, "")))
    return observedText.replace("/", " : ")
  const ratio = Number(rawRatio)
  if (Number.isFinite(ratio) && ratio > 0) return formatSystoleDiastoleDisplay(ratio)
  const parsed = parseSdObservedRatio(observedText)
  return Number.isFinite(parsed) ? formatSystoleDiastoleDisplay(parsed) : observedText
}

export function normalizeCardiacCycleEvidenceFinding(finding, observed) {
  const text = String(finding || "").trim()
  const timingDisplay = cardiacCycleDisplayFromRatioOrObserved(null, observed)
  if (!text) return `Cardiac cycle timing ${timingDisplay}.`
  const ratioMatch =
    text.match(/(?:mean\s+)?systolic\/diastolic ratio\s+(-?\d+(?:\.\d+)?)/i) ||
    text.match(/\bS\/D(?:\s+ratio)?\s+(-?\d+(?:\.\d+)?)/i)
  const display = ratioMatch ? cardiacCycleDisplayFromRatioOrObserved(ratioMatch[1], observed) : timingDisplay
  // The durations keep the statistic the finding names (mean or median), or
  // none when it names none
  const phaseMatch =
    /\b(?:(mean|median)\s+)?systole\s+(\d+(?:\.\d+)?)\s*ms\s+and\s+diastole\s+(\d+(?:\.\d+)?)\s*ms/i.exec(text)
  if (phaseMatch) {
    const statistic = phaseMatch[1] ? `${phaseMatch[1].toLowerCase()} ` : ""
    return `Cardiac cycle timing ${display}; ${statistic}systole ${phaseMatch[2]} ms and diastole ${phaseMatch[3]} ms across accepted cycles.`
  }
  if (ratioMatch) return `Cardiac cycle timing ${display}.`
  return text.replace(/\bin the usable segment\b/gi, "across accepted cycles")
}
